import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Envelope, X, ArrowClockwise } from "@phosphor-icons/react";

/**
 * EmailVerificationBanner — banner non invasivo che ricorda all'utente
 * di confermare il proprio indirizzo email + bottone "Reinvia link".
 *
 * Si nasconde se:
 *   - l'utente è verificato (`email_verified === true`)
 *   - l'utente l'ha esplicitamente chiusa con la X (memorizzato in
 *     sessionStorage, riappare al prossimo login)
 *   - l'utente si è loggato con Google (sempre `email_verified=true`)
 */
export default function EmailVerificationBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("pronto:verify-dismissed") === "1",
  );
  const [busy, setBusy] = useState(false);

  if (!user) return null;
  if (user.email_verified) return null;
  if (dismissed) return null;

  const resend = async () => {
    setBusy(true);
    try {
      await api.post("/auth/resend-verification", { email: user.email });
      toast.success("Email reinviata. Controlla anche in spam/promozioni.");
    } catch (err) {
      toast.error(apiError(err, "Impossibile reinviare l'email"));
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    sessionStorage.setItem("pronto:verify-dismissed", "1");
    setDismissed(true);
  };

  return (
    <div
      data-testid="email-verify-banner"
      className="relative bg-[#FFF6E0] border-b border-[#E6D9A8] text-[#5C4A1F] text-[12.5px] sm:text-[13px]"
    >
      {/* iter v29 r208 — Su mobile (iPhone) il banner divorava 1/3 dello
          schermo per un messaggio non bloccante. Comprimo: una sola riga,
          email troncata, CTA "Reinvia" inline. Il testo lungo resta solo
          su desktop. */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 sm:py-2.5 flex items-center gap-2 sm:gap-3">
        <Envelope size={15} weight="duotone" className="text-[#B58E2B] shrink-0" />
        <div className="flex-1 leading-snug min-w-0">
          {/* Mobile: messaggio compatto su 1 riga */}
          <div className="sm:hidden flex items-center gap-1.5 flex-wrap">
            <span className="font-semibold">Verifica l&apos;email.</span>
            <button
              type="button"
              onClick={resend}
              disabled={busy}
              data-testid="email-verify-resend"
              className="inline-flex items-center gap-1 underline underline-offset-2 font-semibold text-[#1C1C1A] hover:text-[#7C8B6E] disabled:opacity-50"
            >
              <ArrowClockwise size={11} weight="bold" className={busy ? "animate-spin" : ""} />
              Reinvia link
            </button>
          </div>
          {/* Desktop: messaggio completo */}
          <div className="hidden sm:block">
            <span className="font-semibold">Conferma il tuo indirizzo email.</span>{" "}
            Ti abbiamo inviato un link a <strong>{user.email}</strong>. Non lo trovi?
            Controlla in <em>spam/promozioni</em> oppure
            {" "}
            <button
              type="button"
              onClick={resend}
              disabled={busy}
              data-testid="email-verify-resend-desktop"
              className="inline-flex items-center gap-1 underline underline-offset-2 font-semibold text-[#1C1C1A] hover:text-[#7C8B6E] disabled:opacity-50"
            >
              <ArrowClockwise size={11} weight="bold" className={busy ? "animate-spin" : ""} />
              reinvia il link
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          data-testid="email-verify-dismiss"
          className="shrink-0 p-1 rounded hover:bg-[#FFE9B0] text-[#7C5B1F]"
          aria-label="Chiudi"
          title="Chiudi (riappare al prossimo accesso)"
        >
          <X size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}
